import { userRoles } from "app/publicDictionaryValues";
import { clsx, type ClassValue } from "clsx";
import AppSetting from "config/AppSetting";
import { twMerge } from "tailwind-merge";
import { globalPendingRequests } from "./rest/utils";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type ServiceCallback<T> = (result: T) => void;
type ErrorCallback = (error: Error) => void;
type ServiceFunction<TPayload, TResult> = (
  payload: TPayload,
  successCallback: ServiceCallback<TResult>,
  errorCallback: ErrorCallback,
  returnPara?: any
) => void;

const wrapRequestToPromise = <TPayload, TResult>(serviceFn: ServiceFunction<TPayload, TResult>) => {
  return (payload: TPayload): Promise<TResult> => {
    return new Promise((resolve, reject) => {
      serviceFn(
        payload,
        (result: TResult) => resolve(result),
        (error: Error) => reject(error)
      );
    });
  };
};

const reloadMessage = (props: {
  type: "success" | "warning" | "error";
  message: string;
  duration?: number;
}) => {
  sessionStorage.setItem("pmp_reload_message", JSON.stringify(props));
};

const isAsAndAbove = () => {
  const user = AppSetting.localStorageValue.user.get();
  if (!user) return false;

  const loginUserRoles: { projectID: string; roleID: string }[] = user?.roles || [];
  const roleIDs = loginUserRoles.map((i) => i.roleID);

  const rolesList = [
    userRoles.Manager,
    userRoles.Assistant,
    userRoles.TeamLeader,
    userRoles.Specialist,
  ];
  return (
    roleIDs?.findIndex((roleItem) => rolesList.findIndex((item) => item === roleItem) !== -1) > -1
  );
};

const handleUserRole = (roleModel: { roleID: string; roleName: string }[]) => {
  if (roleModel?.length === undefined || roleModel?.length === 0) {
    return false;
  }
  return roleModel.some((x) => x.roleID === userRoles.OpExAndTOO || x.roleID === userRoles.Opex);
};

const handleUserRoleOnlyOpex = (roleModel: { roleID: string; roleName: string }[]) => {
  if (roleModel?.length === undefined || roleModel?.length === 0) {
    return false;
  }
  return roleModel.some((x) => x.roleID === userRoles.Opex);
};

const isDashboardJobRole = () => {
  const user = AppSetting.localStorageValue.user.get();
  if (!user) return false;

  const loginUserRoles: { projectID: string; roleID: string }[] = user?.roles || [];
  const roleIDs = loginUserRoles.map((i) => i.roleID);

  const rolesList = [userRoles.OA, userRoles.CE, userRoles.PE, userRoles.CEManager];
  return roleIDs.some((roleItem) => rolesList.includes(roleItem));
};

/**
 * Whether the current user may access the PMP Power BI dashboard.
 * Eligible when the user holds ANY of: OPEX / OPEX+TOO AD group role (from roleModel),
 * Account Specialist and above, or an OA / CE / PE / CEManager account role.
 * All signals are derived from the login response stored in local storage.
 */
const canAccessDashboard = () => {
  const roleModel = AppSetting.localStorageValue.role.get();
  return handleUserRole(roleModel) || isAsAndAbove() || isDashboardJobRole();
};

/**
 * Whether the `/PMPDashboard` item is present in the cached left-menu.
 * The backend populates this menu with items the current user is eligible for,
 * including PO/PPO users who do not hold any of the role-based signals above.
 */
const isDashboardInMenu = () => {
  const menus = AppSetting.localStorageValue.menus.get() as { url: string }[] | undefined;
  return Array.isArray(menus) && menus.some((menu) => menu.url === "/PMPDashboard");
};

const generateGUID = () => {
  let crypto = window.crypto || (window as any).msCrypto;
  if (!crypto) {
    console.log("Crypto API not supported");
    return Date.now().toString(16);
  }
  let buf = new Uint16Array(8);
  crypto.getRandomValues(buf);
  return (
    buf[0].toString(16) +
    buf[1].toString(16) +
    "-" +
    buf[2].toString(16) +
    "-" +
    buf[3].toString(16) +
    "-" +
    buf[4].toString(16) +
    "-" +
    buf[5].toString(16) +
    buf[6].toString(16) +
    buf[7].toString(16)
  );
};

/**
 *
 * Disables pointer events, preventing user interaction.
 */
const disableInteraction = () => {
  let blocker = document.getElementById("global-interaction-blocker");

  const checkAndRestoreInteraction = () => {
    if (globalPendingRequests.size === 0) {
      document.getElementById("global-interaction-blocker")?.remove();
    } else {
      setTimeout(checkAndRestoreInteraction, 100);
    }
  };

  if (!blocker) {
    blocker = document.createElement("div");
    blocker.id = "global-interaction-blocker";
    Object.assign(blocker.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100vw",
      height: "100vh",
      background: "rgba(0, 0, 0, 0)",
      zIndex: "999999",
    });
    document.body.appendChild(blocker);
    // Start checking for pending requests
    setTimeout(checkAndRestoreInteraction, 100);
  }
};

const enableInteraction = async () => {
  while (globalPendingRequests.size > 0) {
    await new Promise((r) => setTimeout(r, 16));
  }
  document.getElementById("global-interaction-blocker")?.remove();
};

export {
  cn,
  wrapRequestToPromise,
  handleUserRole,
  handleUserRoleOnlyOpex,
  canAccessDashboard,
  isDashboardInMenu,
  reloadMessage,
  isAsAndAbove,
  generateGUID,
  disableInteraction,
  enableInteraction,
};
export type { ServiceFunction };
